import { useEffect, useState } from "react";
import { Minus, Plus, Star, ChevronDown, Sparkles, Shuffle, Archive } from "lucide-react";
import { toast } from "sonner";
import { api, gbp, ukDate, errorMessage } from "../../lib/api";
import { useOrder, priceFor } from "../../context/OrderContext";
import { useAuth } from "../../context/AuthContext";
import { useIsTouch } from "../../hooks/useIsTouch";
import SwipeToDeleteRow from "./SwipeToDeleteRow";
import { PurchaseUnitPicker, BoxSavingHint, boxSaving, defaultPurchaseUnit } from "./PurchaseUnitPicker";

// Weight / volume billing units that may be ordered as fractional quantities (e.g. 2.5 kg).
// Everything else (ea, box, case, bottle, can…) is treated as whole-unit only.
const FRACTIONAL_UNITS = new Set(["kg", "kgs", "g", "gr", "grm", "l", "lt", "ltr", "litre", "liter", "ml", "cl"]);

export function fmtQty(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "0";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

export function isFractionalUnit(unit) {
  return FRACTIONAL_UNITS.has(String(unit || "").trim().toLowerCase());
}

export function QtyControl({ value, onChange, testId, allowFraction = false }) {
  const [local, setLocal] = useState(fmtQty(value || 0));
  const [editing, setEditing] = useState(false);

  // Keep the input in sync when the quantity changes from outside (e.g. +/- buttons, supplier swap).
  useEffect(() => { if (!editing) setLocal(fmtQty(value || 0)); }, [value, editing]);

  const commit = () => {
    setEditing(false);
    const raw = local.replace(",", ".").trim();
    if (raw === "" || raw === "." || raw === "-") { setLocal(fmtQty(value || 0)); return; }
    let n = parseFloat(raw);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (!allowFraction) n = Math.round(n);
    // Keep up to 3 decimals for weight/volume orders to avoid floating-point noise.
    if (allowFraction) n = Math.round(n * 1000) / 1000;
    setLocal(fmtQty(n));
    if (n !== (value || 0)) onChange(n);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
    if (e.key === "Escape") { setLocal(fmtQty(value || 0)); setEditing(false); e.currentTarget.blur(); }
  };

  // Allow only digits + optional single decimal separator while typing (prevents letters, negatives, '-' etc.).
  const onInput = (e) => {
    const cleaned = e.target.value
      .replace(/,/g, ".")
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");
    const parts = cleaned.split(".");
    const next = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    setLocal(allowFraction ? next : next.split(".")[0]);
  };

  const step = (delta) => {
    const next = Math.max(0, (Number(value) || 0) + delta);
    onChange(allowFraction ? Math.round(next * 1000) / 1000 : Math.round(next));
  };

  const empty = !(Number(value) > 0);

  return (
    <div className="flex items-center gap-1 shrink-0" data-testid={testId}>
      <button onClick={() => step(-1)} className="h-12 w-12 rounded-full bg-slate-100 grid place-items-center active:bg-slate-200 disabled:opacity-40" disabled={empty} data-testid={`${testId}-minus`} aria-label="Decrease"><Minus className="h-5 w-5" /></button>
      <input
        type="text"
        inputMode={allowFraction ? "decimal" : "numeric"}
        pattern={allowFraction ? "[0-9]*[.,]?[0-9]*" : "[0-9]*"}
        enterKeyHint="done"
        value={local}
        onChange={onInput}
        onFocus={(e) => { setEditing(true); e.target.select(); }}
        onBlur={commit}
        onKeyDown={onKeyDown}
        aria-label="Quantity"
        className={`w-14 h-12 text-center font-display text-xl font-bold rounded-md border border-slate-200 bg-slate-50 focus:bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none tabular-nums ${empty ? "text-slate-400" : "text-slate-900"}`}
        data-testid={`${testId}-input`}
      />
      <button onClick={() => step(1)} className="h-12 w-12 rounded-full bg-emerald-500 text-white grid place-items-center active:bg-emerald-600" data-testid={`${testId}-plus`} aria-label="Increase"><Plus className="h-5 w-5" /></button>
    </div>
  );
}

export default function ProductCard({ group, onChanged }) {
  const { can } = useAuth();
  const isTouch = useIsTouch();
  // Only admins/owners can archive supplier products — the API itself requires manage_suppliers, but we
  // also hide the swipe affordance for staff so the gesture never resolves into a confusing 403.
  const canArchive = can("manage_suppliers");
  const notSame = async (o) => {
    try {
      await api.post("/product-matches/not-same", { a: group.recommended.supplier_product_id, b: o.supplier_product_id });
      toast.success(`"${o.description}" will no longer be suggested for ${group.name}`);
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  // Swipe-to-archive. Works on BOTH:
  //  - the top (picked) product of this card — a left-swipe on the main row archives the currently
  //    selected supplier product only (never bleeds into the dropdown alternatives);
  //  - any alternative inside the "Change product" dropdown — each row has its own nested swipe.
  // Hidden from staff and the API still enforces manage_suppliers, so a mis-swipe can't resolve to a 403.
  const archiveProduct = async (o) => {
    try {
      await api.post("/supplier-products/archive", { product_ids: [o.supplier_product_id], archived: true });
      toast.success(`"${o.display_name || o.description}" archived from ${o.supplier_name}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/supplier-products/archive", { product_ids: [o.supplier_product_id], archived: false });
              toast.success(`"${o.display_name || o.description}" restored`);
              onChanged?.();
            } catch (e) { toast.error(errorMessage(e)); }
          },
        },
      });
      onChanged?.();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const { items, setQty, setUnit, swapSupplier } = useOrder();
  const [open, setOpen] = useState(false);
  const picked = group.options.find((o) => items[o.supplier_product_id]) || group.recommended;
  const defUnit = defaultPurchaseUnit(picked);
  const line = items[picked.supplier_product_id];
  const curUnit = line?.purchase_unit || defUnit;
  // the offer priced in the chosen purchase unit (BOX £32.99 vs EACH £8.99) — one unit per line, never mixed
  const lane = curUnit ? (picked.purchase_units || []).find((u) => u.key === curUnit) : null;
  const selected = { ...picked, default_purchase_unit: defUnit, ...(curUnit ? { price: priceFor(picked, curUnit).price, unit: priceFor(picked, curUnit).unit } : {}),
    ...(lane?.unit_price != null ? { unit_price: lane.unit_price, unit_label: lane.unit_label, basis: lane.basis || picked.basis } : {}) };  // comparable price of the CHOSEN purchase unit (EACH £5.99 → £5.99/kg, not the BOX's)
  const qty = line?.quantity || 0;
  const units = picked.purchase_units || [];
  const saving = boxSaving(units, qty, curUnit);
  // Every other option in the group — same-supplier siblings (BOX / EACH / SBOX of the same product) and cross-supplier
  // alternatives — go into one "Change product" list, SORTED cheapest-first by the comparable unit price (per-kg / per-litre
  // / per-item), so the owner always sees the cheapest linked alternative at the top. Options missing a unit price fall to
  // the bottom (so apples-to-apples prices win over "no price yet" rows). Confirmed matches and AI "possible matches" are
  // sorted TOGETHER (not confirmed-first-then-possible) so the cheapest row is always on top regardless of match type.
  const alternatives = group.options
    .filter((o) => o.supplier_product_id !== picked.supplier_product_id)
    .map((o) => ({ ...o, possible: false }))
    .concat((group.possible || []).map((o) => ({ ...o, possible: true })))
    .sort((a, b) => {
      const av = a.unit_price != null ? a.unit_price : Infinity;
      const bv = b.unit_price != null ? b.unit_price : Infinity;
      if (av !== bv) return av - bv;
      return (a.price ?? Infinity) - (b.price ?? Infinity);
    });
  // Title / subtitle follow the current pick, not the group's canonical name — so when the owner picks "BURCU" below,
  // the title at the top changes to "BURCU ROASTED AUBERGINE" and the supplier line stays accurate.
  const title = picked.display_name || picked.description || group.name;
  // CHEAPEST badge honours ALL visible siblings (confirmed options + unconfirmed "possible" matches on the same unit basis).
  // If an unconfirmed match looks cheaper, we hide the badge and surface a hint instead of falsely crowning the recommended option.
  const samePossible = (group.possible || []).filter((p) => p.unit_label === selected.unit_label && p.unit_price != null && selected.unit_price != null);
  const cheaperPossible = samePossible.filter((p) => p.unit_price < selected.unit_price - 0.001).sort((a, b) => a.unit_price - b.unit_price)[0];
  const isCheapestConfirmed = selected.supplier_product_id === group.recommended.supplier_product_id && group.options.length > 1;
  const isCheapest = isCheapestConfirmed && !cheaperPossible && !group.cheaper_alternative && group.comparable !== false;
  const notComparable = group.options.filter((o) => o.not_comparable);
  // approved ALTERNATIVE products (separate records) that beat the selected option per kg / litre / item
  const cheaperAlt = group.cheaper_alternative && selected.unit_price != null && group.cheaper_alternative.basis === selected.basis && group.cheaper_alternative.unit_price < selected.unit_price - 0.001
    ? { ...group.cheaper_alternative, saving_pct: Math.round((selected.unit_price - group.cheaper_alternative.unit_price) / selected.unit_price * 1000) / 10 } : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`product-card-${group.id}`}>
      <SwipeToDeleteRow
        enabled={isTouch && canArchive}
        onDelete={() => archiveProduct(picked)}
        testId={`swipe-archive-card-${picked.supplier_product_id}`}
        deleteLabel={`Archive ${title} from ${selected.supplier_name}`}
        actionIcon={<Archive className="h-5 w-5" aria-hidden="true" />}
        actionClassName="bg-amber-600 active:bg-amber-700 focus-visible:ring-amber-300"
      >
        <div className="flex items-start gap-3 p-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 leading-snug" data-testid="product-name">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{selected.supplier_name}{selected.pack_size && !title.toLowerCase().includes(selected.pack_size.toLowerCase()) ? ` · ${selected.pack_size}` : ""}</p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-sm">
            <span className="font-mono font-semibold text-slate-900" data-testid="product-order-price">{gbp(selected.price)}{selected.unit ? <span className="text-xs font-sans font-medium text-slate-500"> / {selected.unit.length <= 5 && selected.unit === selected.unit.toUpperCase() ? selected.unit : selected.unit.toLowerCase()}</span> : null}</span>
            {selected.unit_price != null && selected.unit_label !== "per pack" && selected.unit_label !== `per ${(selected.unit || "").toLowerCase()}` && <span className="text-xs text-slate-500 font-mono" data-testid="product-compare-price">{gbp(selected.unit_price)} {selected.unit_label}</span>}
            {isCheapest && <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5" data-testid="cheapest-badge"><Star className="h-3 w-3 fill-amber-500 text-amber-500" /> CHEAPEST</span>}
            {isCheapestConfirmed && group.comparable === false && notComparable.length > 0 && <span className="text-[11px] text-slate-500" title={`Can't compare: ${notComparable.map((o) => `${o.supplier_name} — ${o.not_comparable}`).join("; ")}`} data-testid="not-comparable-note">can't compare {notComparable.map((o) => o.supplier_name).join(", ")} — {notComparable[0].not_comparable}</span>}
            {group.ai_matched && <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-1.5" data-testid="ai-matched-badge" title="Suppliers were matched by AI and confirmed by you"><Sparkles className="h-3 w-3" /> AI matched</span>}
            {selected.extra_per_pack > 0 && <span className="text-[11px] text-rose-700" data-testid="cheaper-available">Cheaper option available</span>}
          </div>
          {cheaperPossible && (
            <p className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="possibly-cheaper-hint">
              Possibly cheaper: <span className="font-semibold">{cheaperPossible.supplier_name}</span> at <span className="font-mono">{gbp(cheaperPossible.unit_price)} {cheaperPossible.unit_label}</span> ({gbp(cheaperPossible.price)}). Confirm it is the same product in the dropdown below to make it the recommended choice.
            </p>
          )}
          {cheaperAlt && (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1" data-testid="cheaper-alternative-hint">
              <span><Shuffle className="inline h-3 w-3 mr-1 -mt-0.5" />Cheaper alternative: <span className="font-semibold">{cheaperAlt.display_name || cheaperAlt.description}</span> ({cheaperAlt.supplier_name}) — <span className="font-mono">{gbp(cheaperAlt.unit_price)} {cheaperAlt.unit_label}</span>, {gbp(cheaperAlt.price)} / {(cheaperAlt.unit || "pack").toLowerCase()} · {cheaperAlt.saving_pct}% less</span>
              <button type="button" onClick={() => swapSupplier(selected.supplier_product_id, cheaperAlt, Math.max(1, qty))} className="font-semibold underline" data-testid="use-alternative-button">Use it</button>
            </p>
          )}
          {units.length > 1 && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <PurchaseUnitPicker units={units} value={curUnit} onChange={(k) => (qty > 0 ? setUnit(selected, k, qty) : setQty({ ...selected, default_purchase_unit: k }, 1))} testId={`purchase-unit-${group.id}`} />
              {units.find((u) => u.key === curUnit)?.items_per_unit && curUnit === "unit" && <span className="text-[11px] text-slate-500" data-testid={`purchase-unit-hint-${group.id}`}>1 {units.find((u) => u.key === "unit").label.toUpperCase()} = {units.find((u) => u.key === "unit").items_per_unit} items</span>}
              <span className="text-[11px] text-slate-400" data-testid={`purchase-unit-date-${group.id}`}>{units.find((u) => u.key === curUnit)?.source === "price_list" ? "Manual Uploaded Price" : "last invoiced"} {ukDate(units.find((u) => u.key === curUnit)?.price_date)}</span>
            </div>
          )}
          <BoxSavingHint saving={saving} onSwitch={() => setUnit(selected, "unit", saving.boxes)} testId={`box-saving-${group.id}`} />
          {units.length <= 1 && selected.price_age && <p className="text-[11px] text-slate-400" data-testid={`price-age-${group.id}`}>{selected.price_source === "price_list" ? `Manual Uploaded Price: ${ukDate(selected.price_date)}` : selected.price_age}</p>}
        </div>
        <QtyControl value={qty} onChange={(v) => setQty(selected, v)} testId={`qty-${group.id}`} allowFraction={isFractionalUnit(selected.unit)} />
        </div>
      </SwipeToDeleteRow>
      {alternatives.length > 0 && (
        <div className="px-3 pb-3">
          <button onClick={() => setOpen((v) => !v)} className="text-xs font-medium text-emerald-700 flex items-center gap-1" data-testid={`change-supplier-${group.id}`}>
            Change product ({alternatives.length}) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ul className="mt-2 space-y-1.5" data-testid={`supplier-options-${group.id}`}>
              {alternatives.map((o) => (
                <li key={o.supplier_product_id}>
                  <SwipeToDeleteRow
                    enabled={isTouch && canArchive}
                    onDelete={() => archiveProduct(o)}
                    testId={`swipe-archive-${o.supplier_product_id}`}
                    deleteLabel={`Archive ${o.display_name || o.description} from ${o.supplier_name}`}
                    actionIcon={<Archive className="h-5 w-5" aria-hidden="true" />}
                    actionClassName="bg-amber-600 active:bg-amber-700 focus-visible:ring-amber-300"
                  >
                    <div className={o.possible ? "flex items-stretch gap-1.5 bg-white" : "bg-white"}>
                      <button onClick={() => { swapSupplier(selected.supplier_product_id, o, Math.max(1, qty)); setOpen(false); }} className={`w-full min-w-0 text-left flex items-center justify-between gap-2 rounded-lg border px-3 py-2.5 active:bg-slate-50 ${o.possible ? "border-amber-200 bg-amber-50/30" : "border-slate-200"}`} data-testid={`option-${o.supplier_product_id}`}>
                        <span className="min-w-0"><span className="block text-sm font-medium truncate">{o.display_name || o.description}</span><span className="block text-xs text-slate-500 truncate">{o.supplier_name}{o.pack_size && !(o.display_name || o.description || "").toLowerCase().includes(o.pack_size.toLowerCase()) ? ` · ${o.pack_size}` : ""} {o.possible ? <span className="text-amber-700 font-semibold">· Possible match — confirm to compare</span> : o.ai_matched ? <span className="text-slate-500">· AI matched</span> : null}</span></span>
                        <span className="text-right shrink-0"><span className="block font-mono text-sm">{gbp(o.price)}</span>{o.unit_price != null && o.unit_label !== "per pack" && <span className={`block text-[11px] font-mono ${!o.possible && cheaperPossible && o.supplier_product_id === cheaperPossible.supplier_product_id ? "text-amber-800 font-semibold" : "text-slate-500"}`}>{gbp(o.unit_price)} {o.unit_label}</span>}<span className="block text-[11px] text-slate-500" data-testid={`option-last-invoice-${o.supplier_product_id}`}>{o.price_source === "price_list" && o.price_date ? `Manual Uploaded Price: ${ukDate(o.price_date)}` : `Last invoice: ${o.price_date ? ukDate(o.price_date) : "Date unavailable"}`}</span>{!o.possible && o.supplier_product_id === group.recommended.supplier_product_id && group.comparable !== false && <span className="block text-[11px] text-amber-700">⭐ Cheapest confirmed</span>}{o.not_comparable && <span className="block text-[11px] text-slate-400" data-testid={`option-not-comparable-${o.supplier_product_id}`}>{o.not_comparable} — can't compare</span>}{o.possible && cheaperPossible && o.supplier_product_id === cheaperPossible.supplier_product_id && <span className="block text-[11px] text-amber-700 font-semibold">↓ Possibly cheaper</span>}</span>
                      </button>
                      {o.possible && can("manage_suppliers") && <button type="button" onClick={() => notSame(o)} className="shrink-0 w-14 rounded-lg border border-slate-200 px-1 text-[10px] leading-tight font-semibold text-slate-600 hover:bg-slate-50" title="Different product — never suggest it for this one again" data-testid={`not-same-${o.supplier_product_id}`}>Not the same</button>}
                    </div>
                  </SwipeToDeleteRow>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
